"use client"

import type React from "react"

import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Textarea } from "../../components/ui/textarea"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  ArrowLeft,
  Calendar,
  X,
  Archive,
  ArchiveRestore,
  Filter,
  TrendingUp,
  Building2,
  MapPin,
  Clock,
  ArrowRight,
  Check,
  User,
  UserPlus,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  GraduationCap,
  FileText,
  Globe,
  Mail,
  Phone,
  Users2,
  Target,
  Award,
  Zap,
  Loader2,
  QrCode,
  Download,
  Copy,
  EyeOff,
  Eye,
  Link as LinkIcon,
  ClipboardCheck,
  Bot
} from "lucide-react"
import { Switch } from "../../components/ui/switch"
import QRCode from "qrcode"
import { toast } from "react-hot-toast"
import {
  getJobs,
  getAssignableUsers,
  updateJobArchiveStatus,
  deleteJob,
  assignJob,
  getJobDetailsById,
  deleteDraftJob,
  unassignUser,
  updateCampusDriveStatusApi,
  updateActiveInactiveStatusApi,
  getPendingApprovalJobs,
  updateJobApprovalStatus,
} from "../../services/jobsapi"
import { getJobCategoriesList } from "../../services/candidateapi"
import { addHunarSheetJobIdFromDashboardJob, getMappedHunarSheetJobIds } from "../../services/hunarAllocationApi"
import { useRbac } from "../../context"
import {
  type DashboardRestoreState,
  isDashboardRestoreState,
} from "../../utils/dashboardRestoreState"

interface Job {
  createdByBDE?: boolean;
  id: string
  jobId?: string
  title: string
  brandName: string
  companyLogoUrl?: string
  location: string
  employmentType: string
  workType: string
  status: string
  noOfOpenings: number
  createdAt: string
  isArchived?: boolean
  assignedCount?: number
  assignedUserCount?: number
  isExternal?: boolean
  eligibleForCampusDrive?: boolean, 
  link?: string
  minSalary?: number
  maxSalary?: number
  currency?: string
  tenureInDays?: number
  commissionAmount?: number
  commissionType?: string
  isActive?: boolean
  postedBy?: string
  coordinators?: Array<{
    id: string
    username: string
    email: string
    phone: string
  }>
  candidateCount?: number
}

interface JobDetails {
  id: string
  jobId?: string
  title: string
  brandName: string
  companyLogoUrl?: string
  location: string
  employmentType: string
  workType: string
  status: string
  noOfOpenings: number
  createdAt: string
  updatedAt?: string
  description?: string
  requirements?: string[]
  responsibilities?: string[]
  benefits?: string[]
  salaryRange?: {
    min: number
    max: number
    currency: string
  }
  experience?: {
    min: number
    max: number
  }
  skills?: string[]
  education?: string
  contactEmail?: string
  contactPhone?: string
  applicationDeadline?: string
  isRemote?: boolean
  companySize?: string
  industry?: string
  jobType?: string
  isExternal?: boolean
}

interface AssignableUser {
  id: string
  username: string
  name?: string
  email: string
  role: string
  managerUserId?: string
  managerUsername?: string
  assignedOnBehalfOfUserId?: string
  assignedOnBehalfOfUsername?: string
}

interface AssignmentHistory {
  id: string
  userId: string
  username: string
  assignedAt: string
  status: string
}

interface FunnelMappedJob {
  sheetJobId: string
  jobTitle?: string
  openingJobId?: string
  internalJobId?: string
}

const Dashboard = () => {
  const [lastAssignedCount, setLastAssignedCount] = useState<number | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const hasRestoredDashboardRef = useRef(false)

  const [jobs, setJobs] = useState<Job[]>([])
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([])
  const [alreadyAssignedUsers, setAlreadyAssignedUsers] = useState<AssignableUser[]>([])
  const [assignmentHistory, setAssignmentHistory] = useState<AssignmentHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [isLoadingAssignedUsers, setIsLoadingAssignedUsers] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const status = params.get("status")
    if (
      status &&
      ["published", "pendingApproval", "rejected", "drafted", "archived", "private"].includes(status)
    ) {
      return status
    }
    return "published"
  })
  const [publishedFilter, setPublishedFilter] = useState("all")
  const [jobTypeFilter, setJobTypeFilter] = useState("internal")
  const [activeInactiveFilter, setActiveInactiveFilter] = useState("active")
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [selectedATMs, setSelectedATMs] = useState<string[]>([])
  const [assigningJob, setAssigningJob] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("")
  const [qrCodeLoading, setQrCodeLoading] = useState(false)
  const [qrJob, setQrJob] = useState<Job | null>(null)
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('jobsDashboardItemsPerPage')
    return saved ? Number(saved) : 12
  })
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [isSearchFocused, setIsSearchFocused] = useState(true)
  const [coordinatorsDialogOpen, setCoordinatorsDialogOpen] = useState(false)
  const [selectedJobForCoordinators, setSelectedJobForCoordinators] = useState<Job | null>(null)
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false)
  const [selectedJobForPayments, setSelectedJobForPayments] = useState<Job | null>(null)
  const [userSearchTerm, setUserSearchTerm] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("All Categories")
  const [categoryOptions, setCategoryOptions] = useState<Array<{label: string, value: string}>>([])
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const [jobDetailsModalOpen, setJobDetailsModalOpen] = useState(false)
  const [selectedJobDetails, setSelectedJobDetails] = useState<JobDetails | null>(null)
  const [loadingJobDetails, setLoadingJobDetails] = useState(false)
  const [userRole, setUserRole] = useState<string | null>(null)
  const [unassigningUserId, setUnassigningUserId] = useState<string | null>(null)
  const [showAllJobsLink, setShowAllJobsLink] = useState(false)
  const [showAllJobsLinkAddress, setShowAllJobsLinkAddress] = useState("")
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0) // Mock count for jobs seeking approval
  const [approvalsDialogOpen, setApprovalsDialogOpen] = useState(false)
  const [approvalSearchTerm, setApprovalSearchTerm] = useState("")
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
  const [selectedJobForAction, setSelectedJobForAction] = useState<Job | null>(null)
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null)
  const [rejectComment, setRejectComment] = useState("")
  const [rejectCommentError, setRejectCommentError] = useState("")
  const [pendingApprovalJobs, setPendingApprovalJobs] = useState<Job[]>([])
  const { user } = useRbac()

  const effectiveRole = (user?.role || userRole || "").toLowerCase()
  const isAdminUser = effectiveRole === "admin"
  const canPostJobs = Boolean(user?.canPostJobs)
  const canFilterApprovalStatuses =
    ["master_bde", "freelance_bde", "bde_intern"].includes(effectiveRole) ||
    canPostJobs
  const canCreateJobOnDashboard =
    ["admin", "master_bde", "bde_intern", "freelance_bde"].includes(effectiveRole) ||
    canPostJobs

  const allowedPaymentRoles = ['shm', 'hm', 'agency', 'admin', 'fbde', 'consultant']
  const canViewPaymentDetails = allowedPaymentRoles.includes(effectiveRole)
  const canViewAiInterviewBtn = ['TPO','ADMIN', 'COMPANY','MASTER_BDE'].includes(user?.role || '')
  const [funnelMappedJobs, setFunnelMappedJobs] = useState<FunnelMappedJob[]>([])
  const [loadingFunnelMappedJobs, setLoadingFunnelMappedJobs] = useState(false)
  const [addingFunnelJobId, setAddingFunnelJobId] = useState<string | null>(null)
  const [funnelJobIdsDialogOpen, setFunnelJobIdsDialogOpen] = useState(false)
  const [selectedJobForFunnelIds, setSelectedJobForFunnelIds] = useState<Job | null>(null)
  const [customFunnelJobId, setCustomFunnelJobId] = useState("")
  const [funnelJobIdSearch, setFunnelJobIdSearch] = useState("")

  const loadFunnelMappedJobIdsForJob = useCallback(async (jobId: string) => {
    setLoadingFunnelMappedJobs(true)
    try {
      const response = await getMappedHunarSheetJobIds(jobId)
      const jobIds = (response?.data?.jobIds || []).map((id: string) => String(id))
      const mappings: FunnelMappedJob[] = (response?.data?.mappings || [])
        .map((row: FunnelMappedJob) => ({
          sheetJobId: String(row.sheetJobId || ""),
          jobTitle: row.jobTitle,
          openingJobId: row.openingJobId,
          internalJobId: row.internalJobId ? String(row.internalJobId) : undefined,
        }))
        .filter((row: FunnelMappedJob) => row.sheetJobId)
      setFunnelMappedJobs(mappings.length > 0 ? mappings : jobIds.map((sheetJobId: string) => ({ sheetJobId })))
    } catch {
      setFunnelMappedJobs([])
      // Toast is shown by the API client.
    } finally {
      setLoadingFunnelMappedJobs(false)
    }
  }, [])


  const parseJwt = (token: string) => {
    try {
      return JSON.parse(atob(token.split(".")[1]))
    } catch (e) {
      return null
    }
  }

  // Fetch categories from API
  useEffect(() => {
    console.log(user.role,"role==")
    const fetchCategoryOptions = async () => {
      try {
        const response = await getJobCategoriesList();
        setCategoryOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        toast.error('Failed to load categories');
      }
    };
    fetchCategoryOptions();
  }, [])

  useEffect(() => {
    if (user?.role) {
      setUserRole(user.role)
      return
    }
    const token = localStorage.getItem("accessToken")
    if (token) {
      const decodedToken = parseJwt(token)
      if (decodedToken && decodedToken.role) {
        console.log("User role from token:", decodedToken.role)
        setUserRole(decodedToken.role)
      } else {
        console.log("Role not found in token or token is invalid.")
      }
    } else {
      console.log("Access token not found in localStorage.")
    }
  }, [user?.role])


  const fetchPendingApprovalJobs = useCallback(async (search?: string) => {
    if ((user?.role || userRole || "").toLowerCase() !== "admin") {
      return;
    }

    const response = await getPendingApprovalJobs(search)
    if (response.status === "success") {
      setPendingApprovalsCount(response.data?.length || 0)
      setPendingApprovalJobs(response.data || [])
    }
  }, [user?.role, userRole])
  
  useEffect(() => {
    if ((user?.role || userRole || "").toLowerCase() === 'admin') {
       fetchPendingApprovalJobs()
    }
  }, [user?.role, userRole, fetchPendingApprovalJobs])

  // Refetch when search term changes (with debounce)
  useEffect(() => {
    if ((user?.role || userRole || "").toLowerCase() !== "admin") {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchPendingApprovalJobs(approvalSearchTerm)
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [approvalSearchTerm, fetchPendingApprovalJobs, userRole])

  const buildDashboardRestoreState = useCallback((): DashboardRestoreState => ({
    searchTerm,
    currentPage,
    statusFilter,
    categoryFilter,
    jobTypeFilter,
    activeInactiveFilter,
    publishedFilter,
    itemsPerPage,
  }), [
    searchTerm,
    currentPage,
    statusFilter,
    categoryFilter,
    jobTypeFilter,
    activeInactiveFilter,
    publishedFilter,
    itemsPerPage,
  ])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current?.contains(event.target as Node) === false &&
        inputRef.current?.contains(event.target as Node) === false
      ) {
        setIsSearchFocused(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const [usersList, setUsersList] = useState<AssignableUser[]>([])
  const [filteredUsers, setFilteredUsers] = useState(usersList)
  const [assignRoleTab, setAssignRoleTab] = useState<"HM" | "HR">("HM")

  const canAssignHmOrHr = ["shm", "fbde", "consultant"].includes(userRole?.toLowerCase() || "")

  const matchesAssignRoleTab = (role: string) => {
    const r = role?.toUpperCase()
    if (assignRoleTab === "HR") return r === "HR"
    return r === "HM" || r === "AGENCY"
  }

  const fetchAssignableUsers = async (jobId: string) => {
    if (!jobId) return
    try {
      setIsLoadingAssignedUsers(true)
      setAssignRoleTab("HM")
      const response = await getAssignableUsers(jobId)
      if (response.status === "success") {
        // Set assignable users
        if (response.data?.availableToAssign) {
          setAssignableUsers(response.data.availableToAssign)
          setUsersList(response.data.availableToAssign)
          setFilteredUsers(response.data.availableToAssign)
        } else {
          setAssignableUsers([])
          setUsersList([])
          setFilteredUsers([])
        }

        // Set already assigned users from the same response
        if (response.data?.assignedUsers) {
          setAlreadyAssignedUsers(response.data.assignedUsers)
        } else {
          setAlreadyAssignedUsers([])
        }
      } else {
        console.warn("Unexpected response structure:", response)
        setAssignableUsers([])
        setAlreadyAssignedUsers([])
      }
    } catch (error) {
      console.error("Failed to fetch assignable users:", error)
      setAssignableUsers([])
      setAlreadyAssignedUsers([])
      toast.error("Failed to load assignable users. Please try again.")
    } finally {
      setIsLoadingAssignedUsers(false)
    }
  }

  const handleQrDialogOpenChange = (open: boolean) => {
    setIsQrDialogOpen(open)
    if (!open) {
      setQrJob(null)
      setQrCodeDataUrl("")
      setQrCodeLoading(false)
      setShowAllJobsLink(false)
      setShowAllJobsLinkAddress("")
    }
  }

  const handleOpenQrDialog = async (job: Job | null, showAllJobs: boolean = false) => {
    try {
      setQrCodeLoading(true)
      setQrJob(job)
      setIsQrDialogOpen(true)
      const baseUrl = process.env.REACT_APP_NEXT_FRONT_END_URL;
      const qrUrl = showAllJobs
        ? `${baseUrl}/jobs?tpoId=${user?.id}&source=campus-drive`
        : job?.link;

      const dataUrl = await QRCode.toDataURL(qrUrl || "", {
        width: 250,
        margin: 1,
        errorCorrectionLevel: "H",
      });
      if (showAllJobs) {
        setShowAllJobsLink(true)
        setShowAllJobsLinkAddress(`${process.env.REACT_APP_NEXT_FRONT_END_URL}/jobs?tpoId=${user?.id}`)
      }
      setQrCodeDataUrl(dataUrl)
    } catch (error) {
      console.error("Failed to generate QR code:", error)
      toast.error("Failed to generate QR code")
      handleQrDialogOpenChange(false)
    } finally {
      setQrCodeLoading(false)
    }
  }

  const handleCopyQrLink = async () => {
    const linkToCopy = showAllJobsLink ? showAllJobsLinkAddress : qrJob?.link

    if (!linkToCopy) {
      toast.error("No sharable link available right now.")
      return
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(linkToCopy)
      } else {
        const textArea = document.createElement("textarea")
        textArea.value = linkToCopy
        textArea.style.position = "fixed"
        textArea.style.opacity = "0"
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
      }
      toast.success("Link copied to clipboard")
    } catch (error) {
      console.error("Failed to copy link:", error)
      toast.error("Unable to copy link. Please try again.")
    }
  }

  const fetchJobDetails = async (jobId: string) => {
    if (!jobId) {
      console.error("fetchJobDetails: Job ID is undefined or invalid")
      return
    }

    try {
      console.log("Calling fetchJobDetails for jobId:", jobId)
      const response = await getJobDetailsById(jobId)
      console.log("getJobDetailsById response:", response)

      if (response.status === "success") {
        console.log("Raw assignmentHistory:", response.data.job.assignmentsHistory)
        // Map backend assignmentHistory to frontend format
        const mappedHistory = response.data.job.assignmentsHistory.map((item: any) => {
          const obj = {
            id: item.assignedTo?.id || item.id,
            userId: item.assignedTo?.id || "",
            username: item.assignedTo?.username || "",
            assignedAt: item.assignedAt || "",
            status: item.level || item.status || "",
          }
          console.log("AssignedTo Username:", item.assignedTo?.username)
          return obj
        })
        setAssignmentHistory(mappedHistory)
        console.log("Assignment history set:", mappedHistory)
      } else {
        console.warn("fetchJobDetails: Unexpected response structure:", response)
        setAssignmentHistory([])
      }
    } catch (error) {
      console.error("fetchJobDetails: Failed to fetch job details for jobId:", jobId, error)
      setAssignmentHistory([])
      toast.error("Failed to load job details. Please try again.")
    }
  }

  const isFetchingRef = useRef(false)
  const [isRestoringFromNavigation, setIsRestoringFromNavigation] = useState(false)

  const fetchJobs = async (page = 1, restore?: DashboardRestoreState) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log("Already fetching jobs, skipping duplicate call")
      return
    }

    const effectiveSearchTerm = restore?.searchTerm ?? searchTerm
    const effectiveStatusFilter = restore?.statusFilter ?? statusFilter
    const effectiveCategoryFilter = restore?.categoryFilter ?? categoryFilter
    const effectiveJobTypeFilter = restore?.jobTypeFilter ?? jobTypeFilter
    const effectiveActiveInactiveFilter = restore?.activeInactiveFilter ?? activeInactiveFilter
    const effectiveItemsPerPage = restore?.itemsPerPage ?? itemsPerPage
    
    try {
      isFetchingRef.current = true
      setLoading(true)
      setIsLoading(true)
      const params: any = {
        page,
        limit: effectiveItemsPerPage,
        _t: Date.now(),
      }

      if (effectiveSearchTerm.trim()) {
        params.search = effectiveSearchTerm.trim()
      }

      if (effectiveStatusFilter !== "all") {
        params.status = effectiveStatusFilter
      }

      // Add job type filter for admin users - default to internal jobs
      const isAdmin = userRole?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "admin"
      if (isAdmin) {
        if (effectiveJobTypeFilter === "publicApplication") {
          params.isPublicApplication = "true"
        } else if (effectiveJobTypeFilter === "external") {
          params.isExternal = "true"
        } else if (effectiveJobTypeFilter === "all") {
          // Don't send isExternal param to get all jobs (both internal and external)
          // No parameter needed
        } else {
          // Default behavior: fetch internal jobs (when "internal" or any other value)
          params.isExternal = "false"
        }
      }

      if (user?.role?.toLowerCase() === "tpo") {
        params.userRole = user?.role
      }

      if (effectiveCategoryFilter !== "All Categories") {
        params.category = effectiveCategoryFilter
      }

      // Add active/inactive filter for admin users
      if (effectiveActiveInactiveFilter !== "all") {
        if (effectiveActiveInactiveFilter === "active") {
          params.isActive = "true"
        } else if (effectiveActiveInactiveFilter === "inactive") {
          params.isActive = "false"
        }
      }

      const response = await getJobs(params)

      if (response.status === "success" && response.data?.jobs) {
        // Map jobs to convert companyName to brandName if needed
        const mappedJobs = response.data.jobs.map((job: any) => ({
          ...job,
          brandName: job.brandName || '',
        }))
        setJobs(mappedJobs)
        setCurrentPage(response.page)
        setTotalPages(response.totalPages)
        setTotalResults(response.totalResults)
        console.log('✅ Jobs fetched successfully:', {
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
          totalResults: response.totalResults,
          jobsCount: response.data.jobs.length
        })
      } else {
        console.warn("Unexpected response structure:", response)
        setJobs([])
        toast.error("Invalid response from server. Please try again.")
      }
    } catch (error: any) {
      console.error("Failed to fetch jobs:", error)
      setJobs([])
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        toast.error("Invalid pagination parameters. Please refresh the page.")
      } else if (error.response?.status === 401) {
        toast.error("Authentication failed. Please log in again.")
      } else if (error.response?.status === 403) {
        toast.error("Access denied. You don't have permission to view these jobs.")
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.")
      } else {
        toast.error("Failed to load jobs. Please try again.")
      }
    } finally {
      setLoading(false)
      setIsLoading(false)
      isFetchingRef.current = false
    }
  }

  useEffect(() => {
    setCurrentPage(1)
    localStorage.setItem('jobsDashboardItemsPerPage', String(itemsPerPage))
  }, [itemsPerPage])

  // Validate pagination state when itemsPerPage changes
  useEffect(() => {
    if (currentPage > Math.ceil(totalResults / itemsPerPage)) {
      // If current page is beyond the new total pages, reset to page 1
      setCurrentPage(1)
    }
  }, [itemsPerPage, totalResults, currentPage])

  useLayoutEffect(() => {
    if (hasRestoredDashboardRef.current) {
      return
    }

    const navigationState = location.state as {
      dashboardRestore?: DashboardRestoreState
      pageNumber?: number
      page?: number
    } | null

    const restore = navigationState?.dashboardRestore
    const legacyPage = navigationState?.pageNumber ?? navigationState?.page

    if (restore && isDashboardRestoreState(restore)) {
      hasRestoredDashboardRef.current = true
      setIsRestoringFromNavigation(true)
      setSearchTerm(restore.searchTerm)
      setStatusFilter(restore.statusFilter)
      setCategoryFilter(restore.categoryFilter)
      setJobTypeFilter(restore.jobTypeFilter)
      setActiveInactiveFilter(restore.activeInactiveFilter)
      setPublishedFilter(restore.publishedFilter)
      setItemsPerPage(restore.itemsPerPage)
      setCurrentPage(restore.currentPage)
      void fetchJobs(restore.currentPage, restore).finally(() => {
        setIsRestoringFromNavigation(false)
      })
      navigate("/dashboard", { replace: true, state: {} })
      return
    }

    if (legacyPage && legacyPage > 0) {
      hasRestoredDashboardRef.current = true
      setIsRestoringFromNavigation(true)
      setCurrentPage(legacyPage)
      void fetchJobs(legacyPage).finally(() => {
        setIsRestoringFromNavigation(false)
      })
      navigate("/dashboard", { replace: true, state: {} })
    }
  }, [location.state, navigate])

  // Handle page changes and items per page changes
  useEffect(() => {
    if (isRestoringFromNavigation) {
      return
    }
    fetchJobs(currentPage)
  }, [currentPage, itemsPerPage])

  // Handle filter changes
  useEffect(() => {
    if (isRestoringFromNavigation) {
      return
    }
    fetchJobs(currentPage)
  }, [searchTerm, statusFilter, publishedFilter, jobTypeFilter, categoryFilter, activeInactiveFilter])

  const handleShareJob = async (job: Job) => {
    setSelectedJob(job)
    setSelectedATMs([])
    setIsShareDialogOpen(true)
    try {
      await Promise.all([fetchAssignableUsers(job.id), fetchJobDetails(job.id)])
    } catch (error) {
      console.error("Error in handleShareJob:", error)
      toast.error("Failed to load share job data. Please try again.")
    }
  }

const handleAssignJob = async () => {
  if (!selectedJob) {
    toast.error("Please select at least one user to assign the job.")
    return
  }

  try {
    setAssigningJob(true)
    
    const response = await assignJob(selectedJob.id, { assignToUserIds: selectedUsers })
    if (response && response.data && response.data.assignedTo) {
      const assignedWithEmail = response.data.assignedTo.map((user: any) => ({
        ...user,
        email: user.email || "",
      }))
      setAlreadyAssignedUsers(assignedWithEmail)
    }
    
    const count = selectedUsers.length
    setLastAssignedCount(count)
    console.log("Assigned count (frontend):", count)
    setDialogOpen(false)
    
    const assignedNames = getSelectedUserNames().join(", ")
    toast.success(`Job assigned successfully to ${assignedNames}`)
    setSelectedUsers([])
    fetchJobs(currentPage)
  } catch (error) {
    console.error("Failed to assign job:", error)
    toast.error("Failed to assign job. Please try again.")
  } finally {
    setAssigningJob(false)
  }
}

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && !isLoading) {
      setCurrentPage(newPage)
    }
  }

  const renderPagination = () => {
    const pageNumbers = []
    const maxPagesToShow = 6

    let startPage = Math.max(1, currentPage - 2)
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {pageNumbers.map((page) => (
          <Button
            key={page}
            variant={currentPage === page ? "default" : "outline"}
            size="sm"
            onClick={() => handlePageChange(page)}
            disabled={isLoading}
            className={currentPage === page ? "bg-[#EC6E51] text-primary-foreground" : ""}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button variant="outline" size="sm" onClick={() => handlePageChange(totalPages)} disabled={isLoading}>
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    )
  }

  const handleArchiveJob = async (jobId: string, currentArchiveStatus: boolean) => {
    try {
      const newArchiveStatus = !currentArchiveStatus

      setJobs((prevJobs) => prevJobs.map((job) => (job.id === jobId ? { ...job, isArchived: newArchiveStatus } : job)))

      await updateJobArchiveStatus(jobId, newArchiveStatus)

      const action = newArchiveStatus ? "archived" : "unarchived"
      toast.success(`Job has been successfully ${action}`)

      fetchJobs(currentPage)
    } catch (error) {
      console.error("Failed to update archive status:", error)

      setJobs((prevJobs) =>
        prevJobs.map((job) => (job.id === jobId ? { ...job, isArchived: currentArchiveStatus } : job)),
      )

      toast.error("Failed to update archive status. Please try again.")
    }
  }
  const handleUpdateCampusDriveStatus = async (jobId: string, eligibleForCampusDrive: boolean) => {
    try {
      const response = await updateCampusDriveStatusApi(jobId, eligibleForCampusDrive)
      if(response.status === "success"){
        setJobs((prevJobs) => prevJobs.map((job) => (job.id === jobId ? { ...job, eligibleForCampusDrive: eligibleForCampusDrive } : job)))
        toast.success("Campus drive status updated successfully!")
       
      } else {
        toast.error("Failed to update campus drive status. Please try again.")
      }
     
    } catch (error) {
      console.error("Failed to update campus drive status:", error)
      toast.error("Failed to update campus drive status. Please try again.")
    }
  }

  const handleUpdateActiveInactiveStatus = async (jobId: string, isActive: boolean) => {
    try {
      const response = await updateActiveInactiveStatusApi(jobId, isActive)
      if(response.status === "success"){
        // Check if the job should be removed based on current filter
        const shouldRemove = 
          (activeInactiveFilter === "active" && !isActive) || 
          (activeInactiveFilter === "inactive" && isActive)
        
        if (shouldRemove) {
          // Remove the job from the list if it no longer matches the filter
          setJobs((prevJobs) => prevJobs.filter((job) => job.id !== jobId))
          // Update total results count
          setTotalResults((prev) => Math.max(0, prev - 1))
        } else {
          // Update the job in the list
          setJobs((prevJobs) => prevJobs.map((job) => (job.id === jobId ? { ...job, isActive: isActive } : job)))
        }
        toast.success("Active inactive status updated successfully!")
      }
    }catch(error){
      console.error("Failed to update active inactive status:", error)
      toast.error("Failed to update active inactive status. Please try again.")
    }
    }

  const handleDeleteDraftJob = async (jobId: string, event?: React.MouseEvent) => {
    // Prevent default behavior and stop propagation
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    toast((t) => (
      <span>
        Are you sure you want to delete this draft job? This action cannot be undone.
        <br />
        <button
          type="button" // Explicitly set to button to avoid form submission
          style={{
            marginRight: 8,
            padding: "4px 12px",
            background: "#EC6E51",
            color: "white",
            borderRadius: 4,
            border: "none",
          }}
          onClick={async (e) => {
            e.preventDefault()
            e.stopPropagation()
            toast.dismiss(t.id)
            try {
              const token = localStorage.getItem("adminToken") || ""
              await deleteDraftJob(jobId, token)
              setJobs((prevJobs) => prevJobs.filter((job) => job.id !== jobId))
              toast.success("Draft job deleted successfully!")
            } catch (error: any) {
              console.error("Failed to delete draft job:", error)
              toast.error(error.message || "Failed to delete draft job. Please try again.")
            }
          }}
        >
          Delete
        </button>
        <button
          type="button" // Explicitly set to button
          style={{
            padding: "4px 12px",
            background: "#eee",
            color: "#333",
            borderRadius: 4,
            border: "none",
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            toast.dismiss(t.id)
          }}
        >
          Cancel
        </button>
      </span>
    ),
    {
      duration: Infinity, // Keep toast open until user interacts
    })
  }
const handleUnassignUser = async (userId:string,jobId:string)=>{
console.log(selectedJob)
if(!userId || !jobId){
  throw toast.error("Please select a user and a job")
}
  try{
  setUnassigningUserId(userId)
  await unassignUser(userId,jobId)
  toast.success("User unassigned successfully!")
  fetchJobs(currentPage)
  fetchAssignableUsers(jobId)
}catch(error){
  console.error("Failed to unassign user:", error)
  toast.error("Failed to unassign user. Please try again.")
}finally{
  setUnassigningUserId(null)
}
}
  const copyJobLink = () => {
    navigator.clipboard.writeText(`https://example.com/jobs/${selectedJob?.id}`)
    toast.success("Job link copied to clipboard!")
  }

  const handleEditJob = (job: Job) => {
    const timestamp = Date.now()
    navigate(`/create-job?mode=edit&jobId=${job.id}&t=${timestamp}`)
  }

  const handleEditDraft = (jobId: string) => {
    const timestamp = Date.now()
    navigate(`/create-job?mode=edit&jobId=${jobId}&t=${timestamp}`)
  }

  const handleCreateJob = () => {
    navigate("/create-job")
  }

  const handleBackNavigation = () => {
    navigate(-1)
  }

  const handleViewJobDetails = async (jobId: string) => {
    navigate(`/viewdetails/${jobId}`, { state: { dashboardRestore: buildDashboardRestoreState() } })
  }

  const openFunnelJobIdsDialog = (job: Job, event?: React.MouseEvent) => {
    event?.stopPropagation()
    setSelectedJobForFunnelIds(job)
    setCustomFunnelJobId("")
    setFunnelJobIdSearch("")
    setFunnelMappedJobs([])
    setLoadingFunnelMappedJobs(true)
    setFunnelJobIdsDialogOpen(true)
    if (job.id) {
      void loadFunnelMappedJobIdsForJob(job.id)
    } else {
      setLoadingFunnelMappedJobs(false)
    }
  }

  const handleAddFunnelJobId = async (job: Job, sheetJobId?: string) => {
    if (!job.id) return
    const trimmedJobId = String(sheetJobId || "").trim()
    if (trimmedJobId && !/^\d+$/.test(trimmedJobId)) {
      toast.error("Sheet job ID must contain numbers only")
      return
    }
    try {
      setAddingFunnelJobId(trimmedJobId || job.jobId || job.id)
      await addHunarSheetJobIdFromDashboardJob(job.id, trimmedJobId || undefined)
      setCustomFunnelJobId("")
      await loadFunnelMappedJobIdsForJob(job.id)
    } catch {
      // Toast is shown by the API client.
    } finally {
      setAddingFunnelJobId(null)
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "published":
        return "bg-green-100 text-green-800"
      case "drafted":
        return "bg-yellow-100 text-yellow-800"
      case "archived":
        return "bg-gray-100 text-gray-800"
      case "pendingApproval":
        return "bg-orange-100 text-orange-800"
      case "rejected":
        return "bg-red-100 text-red-800"
      default:
        return "bg-blue-100 text-blue-800"
    }
  }

  const getWorkTypeColor = (workType: string) => {
    switch (workType.toLowerCase()) {
      case "remote":
        return "bg-orange-50 text-orange-700 border-orange-200"
      case "hybrid":
        return "bg-blue-50 text-blue-700 border-blue-200"
      case "on-site":
        return "bg-orange-50 text-orange-700 border-orange-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const filteredFunnelMappedJobs = useMemo(() => {
    const query = funnelJobIdSearch.trim().toLowerCase()
    if (!query) return funnelMappedJobs
    return funnelMappedJobs.filter(
      (row) =>
        row.sheetJobId.toLowerCase().includes(query) ||
        (row.jobTitle || "").toLowerCase().includes(query)
    )
  }, [funnelMappedJobs, funnelJobIdSearch])

  const filteredJobs = jobs.filter((job) => {
    if (publishedFilter === "all") return true
    if (publishedFilter === "published") return job.status.toLowerCase() === "published"
    if (publishedFilter === "private") return job.status.toLowerCase() === "private"
    if (publishedFilter === "draft") return job.status.toLowerCase() === "drafted"
    if (publishedFilter === "archived") return job.status.toLowerCase() === "archived"
    return true
  })

  useEffect(() => {
    const term = userSearchTerm.toLowerCase()
    const filtered = usersList.filter((user) => {
      if (canAssignHmOrHr && !matchesAssignRoleTab(user.role)) return false
      if (!term) return true
      return (
        user.username.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.role.toLowerCase().includes(term) ||
        (user.managerUsername?.toLowerCase().includes(term) ?? false)
      )
    })
    setFilteredUsers(filtered)
  }, [userSearchTerm, usersList, assignRoleTab, canAssignHmOrHr])

  const handleUserToggle = (userId: string) => {
    setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    inputRef.current?.focus()
  }

  const getSelectedUserNames = () => {
    return selectedUsers
      .map((id) => {
        const user = usersList.find((u) => u.id === id)
        return user?.username
      })
      .filter((username): username is string => !!username)
  }

  const handleRemoveUser = (userId: string) => {
    handleUserToggle(userId)
  }

  useEffect(() => {
    console.log(selectedUsers)
  }, [selectedUsers])

  useEffect(() => {
    if (user?.role) return
    const userData = localStorage.getItem("user")
    console.log("Raw userData from localStorage:", userData)
    if (userData) {
      try {
        const storedUser = JSON.parse(userData)
        console.log("User role from localStorage:", storedUser.role)
        if (storedUser.role) setUserRole(storedUser.role)
      } catch (error) {
        console.error("Failed to parse user data from localStorage", error)
      }
    } else {
      console.log("No user data found in localStorage.")
    }
  }, [user?.role])
const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const shareableLink = showAllJobsLink ? showAllJobsLinkAddress : qrJob?.link
  return (
    <div className="flex bg-gray-50">
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* <Button variant="ghost" size="sm" className="p-1 sm:p-2" onClick={handleBackNavigation}>
                <ArrowLeft className="h-4 w-4" />
              </Button> */}
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Jobs</h1>
                <p className="text-xs sm:text-sm text-gray-500">
                  <span className="hidden sm:inline">
                    {/* Total Openings: {totalOpenings} |  */}

                  </span>
                  <span>Total Jobs: {totalResults}</span>
                 
                </p>
              </div>
            </div>

            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-64 border-gray-300"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="sm:hidden"
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          <div
            className={`flex flex-col space-y-4 mt-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:space-x-4 ${
              showMobileFilters ? "block" : "hidden sm:flex"
            }`}
          >
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
              {/* Status Filter - Only show full dropdown for admin */}
              {isAdminUser ? (
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-32 border-gray-300">
                    <SelectValue placeholder="Published" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="drafted">Draft</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="pendingApproval">Pending Approval</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              ) : 
              canFilterApprovalStatuses ? (
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40 border-gray-300">
                    <SelectValue placeholder="Published" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="pendingApproval">Pending Approval</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              ):(
                // Non-admin users only see Published jobs
                <Select value="published" disabled>
                  <SelectTrigger className="w-full sm:w-32 border-gray-300">
                    <SelectValue>Published</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full sm:w-44 border-gray-300 text-left [&>span]:line-clamp-none">
                  <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key="All Categories" value="All Categories" className="text-left">All Categories</SelectItem>
                    {categoryOptions.map(category => (
                      <SelectItem key={category.value} value={category.value} className="text-left">
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

            {userRole === "TPO" && <Button
                            variant="outline"
                            size="sm"
                            className="text-xs font-medium rounded-full h-auto bg-accent px-2.5 py-2 sm:p-[10px] min-w-[16px] min-h-[16px] -ml-2.5 hover:bg-orange-600  text-orange-500 hover:text-white"
                            onClick={() => handleOpenQrDialog(null, true)}
                          >
                            <QrCode className="w-3 h-3 sm:w-[13px] sm:h-[13px] cursor-pointer" />
                          </Button>}

              {/* Job Type Filter - Only show for admin */}
              {isAdminUser && (
                <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                  <SelectTrigger className="w-full sm:w-32 border-gray-300">
                    <SelectValue placeholder="Internal" />
                  </SelectTrigger>
                  <SelectContent>
                  <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="all">All Jobs</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                    <SelectItem value="publicApplication">Public Application</SelectItem>
                  </SelectContent>
                </Select>
              )}

                <Select value={activeInactiveFilter} onValueChange={setActiveInactiveFilter}>
                  <SelectTrigger className="w-full sm:w-32 border-gray-300">
                    <SelectValue placeholder="Active" />
                  </SelectTrigger>
                  <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>

                {/* Approvals Button - ADMIN only */}
                {isAdminUser && (
                  <Button 
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 w-full sm:w-auto relative"
                    onClick={() => {
                      setApprovalsDialogOpen(true)
                    }}
                  >
                    <ClipboardCheck className="mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">Approvals</span>
                    <span className="sm:hidden">Approvals</span>
                    {pendingApprovalsCount > 0 && (
                      <Badge 
                        className="ml-2 bg-orange-600 text-white text-xs font-bold px-2 py-0.5 rounded-full"
                      >
                        {pendingApprovalsCount}
                      </Badge>
                    )}
                  </Button>
                )}
                
            </div>
              {canCreateJobOnDashboard && (
              <Button className="bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto" onClick={handleCreateJob}>
                <Plus className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Create New Job</span>
                <span className="sm:hidden">Create Job</span>
              </Button>
            )}
          
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 sm:p-6 max-h-[70vh]">
          {loading ? (
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {[...Array(12)].map((_, index) => (
                <Card
                  key={`shimmer-${index}`}
                  className="group bg-white border-0 shadow-sm rounded-2xl overflow-hidden"
                >
                  <CardContent className="flex flex-col justify-between h-full p-0">
                    <div className="flex flex-col gap-4 p-6 pb-0">
                      {/* Status Badge and Actions */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="animate-pulse bg-gray-200 rounded-full w-20 h-6"></div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="animate-pulse bg-gray-200 rounded-full w-8 h-8"></div>
                          <div className="animate-pulse bg-gray-200 rounded-full w-8 h-8"></div>
                        </div>
                      </div>

                      {/* Job Title and Company */}
                      <div className="space-y-2">
                        <div className="animate-pulse bg-gray-200 rounded w-full h-6 mb-2"></div>
                        <div className="flex items-center gap-2">
                          <div className="animate-pulse bg-gray-200 rounded-full w-7 h-7"></div>
                          <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                        </div>
                      </div>

                      {/* Location and Work Type */}
                      <div className="flex flex-col gap-2">
                        <div className="animate-pulse bg-gray-200 rounded w-full h-8"></div>
                        <div className="flex gap-2">
                          <div className="animate-pulse bg-gray-200 rounded w-24 h-6"></div>
                          <div className="animate-pulse bg-gray-200 rounded w-20 h-6"></div>
                        </div>
                      </div>

                      {/* Job Stats */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex items-center gap-2">
                            <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                            <div className="animate-pulse bg-gray-200 rounded w-8 h-4"></div>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="animate-pulse bg-gray-200 rounded w-4 h-4"></div>
                            <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    <div className="p-6 py-4">
                      <div className="animate-pulse bg-gray-200 rounded-lg w-full h-10"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center px-4">
                <p className="text-gray-500 mb-2">No jobs found</p>
                <p className="text-sm text-gray-400">
                  {searchTerm
                    ? "Try adjusting your search terms"
                    : statusFilter === "pendingApproval"
                      ? "Jobs you submit for approval will appear here"
                      : statusFilter === "rejected"
                        ? "Rejected job submissions will appear here"
                        : "Create your first job to get started"}
                </p>
              </div>
            </div>
          ) : (
            
            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {filteredJobs.map((job) => (
                <Card
                  key={job.id}
                  className="group bg-white border-0 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 rounded-2xl overflow-hidden"
                >
                  <CardContent className="flex flex-col justify-between h-full p-0">
                    <div className="flex flex-col gap-4 p-6 pb-0">
                      <div className="flex  justify-between">

                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            variant="outline"
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${getStatusBadgeColor(job.status)}`}
                          >
                            {job.status === "published" ? "Published" : job.status === "drafted" ? "Draft" : job.status === "pendingApproval" ? "Pending Approval" : job.status === "rejected" ? "Rejected" : "Archived"}
                          </Badge>
                          {job.isExternal && (
                            <Badge
                              variant="outline"
                              className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-orange-100 text-orange-800 border-orange-200"
                            >
                              External
                            </Badge>
                          )}
                          {job.eligibleForCampusDrive && (
                            <Badge
                              variant="outline"
                              className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-blue-100 text-blue-800 border-blue-200"
                            >
                              CD
                            </Badge>
                          )}
                        </div>

                        <div className="flex justify-between items-start">
                          {userRole === "TPO" && (
                            <div className="inline-flex items-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs font-medium rounded-full h-auto bg-accent px-2.5 py-2 sm:p-[10px] min-w-[16px] min-h-[16px] -ml-2.5 hover:bg-orange-600  text-orange-500 hover:text-white"
                            onClick={() => handleOpenQrDialog(job)}
                          >
                            <QrCode className="w-3 h-3 sm:w-[13px] sm:h-[13px] cursor-pointer" />
                          </Button>
                            </div>
                          )}
                          {userRole === "COMPANY" && job.status === "published" && (
                            <div className="inline-flex items-center gap-2">
                              <span className={`text-xs font-medium ${job.isActive ? "text-green-700" : "text-red-700"}`}>
                                {job.isActive ? "Active" : "Inactive"}
                              </span>
                              <Switch
                                checked={job.isActive || false}
                                onCheckedChange={async (checked) => {
                                  try {
                                    await handleUpdateActiveInactiveStatus(job.id, checked)
                                  } catch (error) {
                                    console.error("Error updating active/inactive status:", error)
                                    toast.error("Failed to update status. Please try again.")
                                  }
                                }}
                                className="h-4 w-8 data-[state=checked]:bg-green-600 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-4"
                              />
                            </div>
                          )}
                          {!['bde_intern','freelance_bde','tpo','company','investor','investor'].includes(userRole?.toLowerCase() || '') 
                          && job.status === "published" && !job.isExternal && (
                            <div className="inline-flex items-center">
                              <div
                                className="rounded-full text-black text-xs bg-accent font-bold flex items-center justify-center transition-colors duration-200 border border-input hover:text-accent-foreground"
                                style={{ width: 36, height: 36 }}
                              >
                                {selectedJob?.id === job.id && lastAssignedCount !== null
                                  ? lastAssignedCount
                                  : (job.assignedUserCount ?? 0)}
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs font-medium rounded-full h-auto bg-accent p-[10px] min-w-[16px] min-h-[16px] -ml-2.5 hover:bg-orange-600  text-orange-500 hover:text-white"
                                onClick={async () => {
                                  try {
                                    setSelectedJob(job)
                                    setDialogOpen(true)
                                    setLastAssignedCount(null)
                                    
                                    if (!job.id) {
                                      console.error("Job ID is undefined or invalid")
                                      toast.error("Invalid job ID. Please try again.")
                                      return
                                    }

                                    console.log("Fetching assignable users for job ID:", job.id)
                                    await fetchAssignableUsers(job.id)
                                    
                                  } catch (error) {
                                    console.error("Error in dialog open handler:", error)
                                    toast.error("Failed to load job data. Pleas e try again.")
                                  }
                                }}
                              >
                                <UserPlus className="w-[13px] cursor-pointer" />
                              </Button>
                            </div>
                          )}
                          {["admin", "master_bde"].includes(userRole?.toLowerCase() || '') && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-gray-50 rounded-full transition-colors duration-200"
                              >
                                <MoreHorizontal className="h-4 w-4 text-gray-500" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-50">
                              <DropdownMenuItem onClick={() => handleEditJob(job)} className="cursor-pointer">
                                <Edit className="mr-2 h-4 w-4" />
                                {job.status.toLowerCase() === "drafted" ? "Resume" : "Edit"}
                              </DropdownMenuItem>
                              {statusFilter === "archived" || job.isArchived ? (
                                <DropdownMenuItem onClick={() => handleArchiveJob(job.id, true)} className="cursor-pointer">
                                  <ArchiveRestore className="mr-2 h-4 w-4" />
                                  Unarchive
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleArchiveJob(job.id, false)} className="cursor-pointer">
                                  <Archive className="mr-2 h-4 w-4" />
                                  Archive
                                </DropdownMenuItem>
                              )}
                              {job.eligibleForCampusDrive === true ?(
                                <DropdownMenuItem onClick={() => handleUpdateCampusDriveStatus(job.id, false)} className="cursor-pointer">
                                  <Users2 className="mr-2 h-4 w-4" />
                                 Remove from Campus Drive
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleUpdateCampusDriveStatus(job.id, true)} className="cursor-pointer">
                                  <Users2 className="mr-2 h-4 w-4" />
                                  Add to Campus Drive
                                </DropdownMenuItem>
                              )}
                              {job.isActive ? (
                                <DropdownMenuItem onClick={() => handleUpdateActiveInactiveStatus(job.id, false)} className="cursor-pointer">
                                  <EyeOff className="mr-2 h-4 w-4" />
                                  Mark as Inactive
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleUpdateActiveInactiveStatus(job.id, true)} className="cursor-pointer">
                                  <Eye className="mr-2 h-4 w-4" />
                                  Mark as Active
                                </DropdownMenuItem>
                              )}
                              {job.status.toLowerCase() === "drafted" && (
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  onSelect={(e) => e.preventDefault()} // Prevent default DropdownMenuItem behavior
                                >
                                  <button
                                    type="button"
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      margin: 0,
                                      color: "inherit",
                                      font: "inherit",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                    onClick={() => handleDeleteDraftJob(job.id)} // Pass the event to handleDeleteDraftJob
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </button>
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          )}
                         {(["master_bde","bde_intern","freelance_bde"].includes(userRole?.toLowerCase() || '') || (userRole?.toLowerCase() === "fbde" && job.createdByBDE === true) || (canPostJobs && ["pendingApproval", "rejected"].includes(job.status))) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-gray-50 rounded-full transition-colors duration-200"
                              >
                                <MoreHorizontal className="h-4 w-4 text-gray-500" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-50">
                              <DropdownMenuItem onClick={() => handleEditJob(job)} className="cursor-pointer">
                                <Edit className="mr-2 h-4 w-4" />
                                {job.status.toLowerCase() === "drafted" ? "Resume" : "Edit"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-lg font-bold text-gray-900 line-clamp-2 leading-tight group-hover:text-[#EC6E51] transition-colors duration-200 flex items-center gap-2">
                          {job.title} {!job.isActive && <Badge variant="outline" className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-red-100 text-red-800 border-red-200">Inactive</Badge>}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                       

                            <>
                              {job.companyLogoUrl ? (
                                <img 
                                  src={job.companyLogoUrl} 
                                  alt={`${job.brandName} logo`}
                                  className="h-7 w-7 rounded-full object-cover border border-gray-200"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'block';
                                  }}
                                />
                              ) : (
                                <Building2 className="h-4 w-4 text-gray-400" />
                              )}
                              <Building2 className="h-4 w-4 text-gray-400" style={{display: 'none'}} />
                              <span className="font-medium">{job.brandName}</span>
                            </>
                          
                          
                        </div>
                      </div>

                       <div className="flex flex-col gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs font-medium px-2 py-1 rounded-md bg-gray-50 text-gray-700 border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                              style={{maxWidth:"100%"}}
                              title={`${job.location} (Click to copy)`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(job.location);
                                toast.success("Location copied to clipboard!");
                              }}
                            >
                            <div> <MapPin className="h-[13px] w-[13px] mr-1" /></div>
                              <div className="truncate">
                              {job.location}
                              </div>
                            </Badge>
                            <div className="flex gap-2">
                          {job.employmentType && (
                            <Badge
                              variant="outline"
                              className="text-sm font-medium px-3 py-1.5 rounded-md bg-gray-50 text-gray-700 border-gray-200 flex items-center gap-1"
                              style={{width:"62%"}}
                            >
                              <Clock className="h-4 w-4" />
                              <div className="truncate">
              
                              {job.employmentType}</div>
                            </Badge>
                          )}
                            <Badge
                              variant="outline"
                              className={`text-xs font-medium px-2 py-1 rounded-md ${getWorkTypeColor(job.workType)}`}
                            >
                              <Briefcase className="h-3 w-3 mr-1" />
                              {job.workType}
                            </Badge>
                            
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-center gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">Openings:</span>
                              <span className="text-sm font-bold text-gray-900">{job.noOfOpenings}</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Calendar className="h-4 w-4 text-[#EC6E51]" />
                              <span>{formatDate(job.createdAt)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Coordinators, Payments */}
                        <div className="flex gap-2 flex-wrap">
                          {job.coordinators && job.coordinators.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedJobForCoordinators(job)
                                setCoordinatorsDialogOpen(true)
                              }}
                            >
                              <Users2 className="h-3 w-3 mr-1" />
                              Coordinators ({job.coordinators.length})
                            </Button>
                          )}
                          {(job.minSalary || job.tenureInDays || job.commissionAmount) && user?.role !== "COMPANY" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-xs"
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedJobForPayments(job)
                                setPaymentsDialogOpen(true)
                              }}
                            >
                              <span className="mr-1">₹</span>
                              Payments
                            </Button>
                          )}
                        </div>
                        {user?.role !== "INVESTOR" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full text-xs"
                            onClick={(e) => openFunnelJobIdsDialog(job, e)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Job ID
                          </Button>
                        )}
                      </div>
                      <div className="p-6 py-4 flex flex-col gap-2">
                        {canViewAiInterviewBtn && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-[#EC6E51] hover:text-white hover:bg-[#EC6E51] font-medium px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 border border-[#EC6E51] hover:border-transparent"
                            onClick={() => navigate('/ai-interview', { state: { jobId: job.id } })}
                          >
                            <Bot className="h-4 w-4 shrink-0" />
                            <span className="truncate">AI Interview</span>
                          </Button>
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-[#EC6E51] hover:text-white hover:bg-[#EC6E51] font-medium px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 border border-[#EC6E51] hover:border-transparent"
                          onClick={() => handleViewJobDetails(job.id)}
                        >
                          <span className="truncate">View Details</span>
                          <ArrowRight className="h-4 w-4 shrink-0" />
                        </Button>

                        {user?.role !== "INVESTOR" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-[#EC6E51] hover:text-white hover:bg-[#EC6E51] font-medium px-4 py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 border border-[#EC6E51] hover:border-transparent"
                            onClick={() =>
                              navigate(`/job-candidates/${job.jobId || job.id}`, {
                                state: {
                                  jobTitle: job.title,
                                  dashboardRestore: buildDashboardRestoreState(),
                                },
                              })
                            }
                          >
                            <Users2 className="h-4 w-4 shrink-0" />
                            <span className="truncate">View Candidates ({job.candidateCount ?? 0})</span>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between sticky bottom-0 bg-white p-4 z-30 shadow-lg">
          <div className="text-sm text-gray-500">
            Showing {filteredJobs.length} of {totalResults} jobs
          </div>
          <div className="flex items-center gap-2">
            {totalPages > 1 && renderPagination()}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Per page:</span>
              <Select value={String(itemsPerPage)} onValueChange={(value) => setItemsPerPage(Number(value))}>
                <SelectTrigger className="w-20 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                  <SelectItem value="36">36</SelectItem>
                  <SelectItem value="48">48</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={jobDetailsModalOpen} onOpenChange={setJobDetailsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <div className="bg-white rounded-3xl shadow-2xl w-full">
            {loadingJobDetails ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading job details...</p>
                </div>
              </div>
            ) : selectedJobDetails ? (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {selectedJobDetails.companyLogoUrl && (
                        <img
                          src={selectedJobDetails.companyLogoUrl}
                          alt={`${selectedJobDetails.brandName} logo`}
                          className="w-12 h-12 object-contain rounded-full"
                        />
                      )}
                      <div className="flex-1">
                        <h1 className="text-3xl font-bold mb-2">{selectedJobDetails.title}</h1>
                        <div className="flex items-center gap-4 text-orange-100">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-5 w-5" />
                            <span className="text-lg font-medium">{selectedJobDetails.brandName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>{selectedJobDetails.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setJobDetailsModalOpen(false)}
                      className="text-white hover:bg-white/20 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Job Meta Info */}
                  <div className="flex flex-wrap gap-3 mt-4">
                    <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                      <Briefcase className="h-3 w-3 mr-1" />
                      {selectedJobDetails.workType}
                    </Badge>
                    <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                      <Clock className="h-3 w-3 mr-1" />
                      {selectedJobDetails.employmentType}
                    </Badge>
                    <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                      <Users2 className="h-3 w-3 mr-1" />
                      {selectedJobDetails.noOfOpenings} Opening{selectedJobDetails.noOfOpenings > 1 ? "s" : ""}
                    </Badge>
                    {selectedJobDetails.salaryRange && (
                      <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                        <DollarSign className="h-3 w-3 mr-1" />
                        {selectedJobDetails.salaryRange.currency} {selectedJobDetails.salaryRange.min.toLocaleString()} -{" "}
                        {selectedJobDetails.salaryRange.max.toLocaleString()}
                      </Badge>
                    )}
                    <Badge className={getStatusBadgeColor(selectedJobDetails.status)}>
                      {selectedJobDetails.status.charAt(0).toUpperCase() + selectedJobDetails.status.slice(1)}
                    </Badge>
                    {selectedJobDetails.isExternal && (
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                        External Job
                      </Badge>
                    )}
                    {selectedJobDetails.isRemote && (
                      <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                        <Globe className="h-3 w-3 mr-1" />
                        Remote
                      </Badge>
                    )}
                    {selectedJobDetails.jobType && (
                      <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                        <Briefcase className="h-3 w-3 mr-1" />
                        {selectedJobDetails.jobType}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6 space-y-8">
                  {/* Job Description */}
                  {selectedJobDetails.description && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-orange-500" />
                        <h3 className="text-xl font-semibold text-gray-900">Job Description</h3>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {selectedJobDetails.description}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Requirements */}
                  {selectedJobDetails.requirements && selectedJobDetails.requirements.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-orange-500" />
                        <h3 className="text-xl font-semibold text-gray-900">Requirements</h3>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <ul className="space-y-2">
                          {selectedJobDetails.requirements.map((req, index) => (
                            <li key={index} className="flex items-start gap-2 text-gray-700">
                              <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Responsibilities */}
                  {selectedJobDetails.responsibilities && selectedJobDetails.responsibilities.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-orange-500" />
                        <h3 className="text-xl font-semibold text-gray-900">Responsibilities</h3>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <ul className="space-y-2">
                          {selectedJobDetails.responsibilities.map((resp, index) => (
                            <li key={index} className="flex items-start gap-2 text-gray-700">
                              <ArrowRight className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  {selectedJobDetails.skills && selectedJobDetails.skills.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-orange-500" />
                        <h3 className="text-xl font-semibold text-gray-900">Required Skills</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedJobDetails.skills.map((skill, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="bg-orange-50 text-orange-700 border-orange-200 px-3 py-1"
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  {selectedJobDetails.benefits && selectedJobDetails.benefits.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-orange-500" />
                        <h3 className="text-xl font-semibold text-gray-900">Benefits</h3>
                      </div>
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4">
                        <ul className="space-y-2">
                          {selectedJobDetails.benefits.map((benefit, index) => (
                            <li key={index} className="flex items-start gap-2 text-gray-700">
                              <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Additional Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Experience */}
                    {selectedJobDetails.experience && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <GraduationCap className="h-5 w-5 text-orange-500" />
                          <h4 className="font-semibold text-gray-900">Experience Required</h4>
                        </div>
                        <p className="text-gray-700">
                          {selectedJobDetails.experience.min} - {selectedJobDetails.experience.max} years
                        </p>
                      </div>
                    )}

                    {/* Education */}
                    {selectedJobDetails.education && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <GraduationCap className="h-5 w-5 text-orange-500" />
                          <h4 className="font-semibold text-gray-900">Education</h4>
                        </div>
                        <p className="text-gray-700">{selectedJobDetails.education}</p>
                      </div>
                    )}

                    {/* Company Size */}
                    {selectedJobDetails.companySize && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="h-5 w-5 text-orange-500" />
                          <h4 className="font-semibold text-gray-900">Company Size</h4>
                        </div>
                        <p className="text-gray-700">{selectedJobDetails.companySize}</p>
                      </div>
                    )}

                    {/* Industry */}
                    {selectedJobDetails.industry && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="h-5 w-5 text-orange-500" />
                          <h4 className="font-semibold text-gray-900">Industry</h4>
                        </div>
                        <p className="text-gray-700">{selectedJobDetails.industry}</p>
                      </div>
                    )}

                    {/* Job Type */}
                    {selectedJobDetails.jobType && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="h-5 w-5 text-orange-500" />
                          <h4 className="font-semibold text-gray-900">Job Type</h4>
                        </div>
                        <p className="text-gray-700">{selectedJobDetails.jobType}</p>
                      </div>
                    )}

                    {/* Remote Status */}
                    {selectedJobDetails.isRemote !== undefined && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="h-5 w-5 text-orange-500" />
                          <h4 className="font-semibold text-gray-900">Remote Work</h4>
                        </div>
                        <p className="text-gray-700">{selectedJobDetails.isRemote ? "Yes" : "No"}</p>
                      </div>
                    )}
                  </div>

                  {/* Contact Information */}
                  {(selectedJobDetails.contactEmail || selectedJobDetails.contactPhone) && (
                    <div className="space-y-3">
                      <h3 className="text-xl font-semibold text-gray-900">Contact Information</h3>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                        {selectedJobDetails.contactEmail && (
                          <div className="flex items-center gap-3">
                            <Mail className="h-5 w-5 text-orange-500" />
                            <span className="text-gray-700">{selectedJobDetails.contactEmail}</span>
                          </div>
                        )}
                        {selectedJobDetails.contactPhone && (
                          <div className="flex items-center gap-3">
                            <Phone className="h-5 w-5 text-orange-500" />
                            <span className="text-gray-700">{selectedJobDetails.contactPhone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Dates */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4 text-orange-500" />
                          <span className="text-sm font-medium text-gray-600">Posted</span>
                        </div>
                        <p className="text-gray-900">
                          {new Date(selectedJobDetails.createdAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      {selectedJobDetails.updatedAt && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="h-4 w-4 text-orange-500" />
                            <span className="text-sm font-medium text-gray-600">Last Updated</span>
                          </div>
                          <p className="text-gray-900">
                            {new Date(selectedJobDetails.updatedAt).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      )}
                      {selectedJobDetails.applicationDeadline && (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="h-4 w-4 text-red-500" />
                            <span className="text-sm font-medium text-gray-600">Application Deadline</span>
                          </div>
                          <p className="text-gray-900">
                            {new Date(selectedJobDetails.applicationDeadline).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 p-6 bg-gray-50 rounded-b-3xl">
                  <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={() => setJobDetailsModalOpen(false)} className="px-6">
                      Close
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setJobDetailsModalOpen(false)
                        if (selectedJobDetails) {
                          const jobIdToUse = selectedJobDetails.jobId || selectedJobDetails.id
                          navigate(`/job-candidates/${jobIdToUse}`, {
                            state: {
                              jobTitle: selectedJobDetails.title,
                              dashboardRestore: buildDashboardRestoreState(),
                            },
                          })
                        }
                      }}
                      className="px-6 border-[#EC6E51] text-[#EC6E51] hover:bg-[#EC6E51] hover:text-white"
                    >
                      <Users2 className="h-4 w-4 mr-2" />
                      View Candidates
                    </Button>
                    <Button
                      onClick={() => {
                        setJobDetailsModalOpen(false)
                        if (selectedJobDetails) {
                          handleEditJob(selectedJobDetails as Job)
                        }
                      }}
                      className="bg-orange-500 hover:bg-orange-600 px-6"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Job
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <p className="text-gray-500">Failed to load job details</p>
                  <Button variant="outline" onClick={() => setJobDetailsModalOpen(false)} className="mt-4">
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isQrDialogOpen} onOpenChange={handleQrDialogOpenChange}>
      <DialogContent className="max-w-lg w-[92vw] sm:w-full rounded-2xl border border-orange-100 bg-[#fffaf6] shadow-xl shadow-orange-100/70 p-0">
  {/* HEADER */}
  <div className="px-6 pt-6 pb-4">
    <DialogHeader className="space-y-3 text-left">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100">
          <QrCode className="h-5 w-5 text-orange-600" />
        </div>
        <div>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Share via QR Code
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 leading-relaxed">
            Display or forward this code so candidates can apply instantly.
          </DialogDescription>
        </div>
      </div>

      {qrJob && (
        <div className="rounded-xl border border-orange-100 bg-orange-50/70 px-4 py-3 transition-all">
          <p className="text-sm font-medium text-gray-900">{qrJob.title}</p>
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
            <Building2 className="h-4 w-4 text-gray-500" />
            <span>{qrJob.brandName}</span>
          </div>
        </div>
      )}
    </DialogHeader>
  </div>

  {/* BODY */}
  <div className="px-6 pb-6 space-y-6 border-t border-gray-100 max-w-md mx-auto">
    {/* QR Code Section */}
    <div className="flex justify-center">
      <div className="flex w-full flex-col items-center gap-4 rounded-xl border border-gray-100 bg-gradient-to-b from-gray-50 to-white px-5 py-5">
        {qrCodeLoading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            <p className="text-sm text-gray-500">Preparing QR code...</p>
          </div>
        ) : qrCodeDataUrl ? (
          <>
            <img
              src={qrCodeDataUrl}
              alt="QR Code"
              className="w-44 h-44 rounded-xl border border-gray-200 bg-white p-3"
            />
            <p className="text-xs text-center text-gray-500">
              Print or share this image to direct candidates to the job post.
            </p>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-center text-gray-400">
            <QrCode className="h-10 w-10" />
            <p className="text-sm">QR code is not available.</p>
          </div>
        )}
      </div>
    </div>

    {/* Shareable Link */}
    {shareableLink && (
      <div className="rounded-xl border border-orange-200 bg-orange-50/40 px-4 py-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-orange-600 uppercase tracking-wide">
          <LinkIcon className="h-3.5 w-3.5" />
          <span>Shareable link</span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="flex-1 truncate text-sm text-gray-700">
            {shareableLink}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopyQrLink}
            className="h-9 w-9 text-orange-600 hover:text-orange-700 hover:bg-orange-100/60"
            aria-label="Copy sharable link"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )}

    {/* Action Buttons */}
    <div className="flex flex-col sm:flex-row gap-3">
      {qrCodeDataUrl ? (
        <>
          <Button
            onClick={() => {
              const link = document.createElement("a");
              link.href = qrCodeDataUrl;
              const fileName = qrJob?.title
                ? `qr-${qrJob.title.replace(/\s+/g, "-").toLowerCase()}.png`
                : "qr-code.png";
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            className="flex-1 flex items-center justify-center gap-2 bg-orange-500 text-white hover:bg-orange-600"
          >
            <Download className="h-4 w-4" />
            Download PNG
          </Button>

          <Button
            variant="outline"
            onClick={() => handleQrDialogOpenChange(false)}
            className="flex-1 border-orange-200 text-gray-700 hover:bg-orange-50"
          >
            Close
          </Button>
        </>
      ) : (
        <Button
          variant="outline"
          onClick={() => handleQrDialogOpenChange(false)}
          className="w-full border-orange-200 text-gray-700 hover:bg-orange-50"
        >
          Close
        </Button>
      )}
    </div>
  </div>
</DialogContent>


      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl p-0 max-h-[85vh] overflow-hidden flex flex-col gap-0">
          <div className="bg-white rounded-3xl shadow-2xl w-full flex flex-col max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 shrink-0">
              <DialogHeader className="pr-8 space-y-1">
                <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900 line-clamp-2">
                  Assign <span className="text-orange-600">{selectedJob?.title}</span> Job
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {["shm", "fbde", "consultant"].includes(userRole?.toLowerCase() || "")
                    ? "Assign to HMs, or to HRs under your HMs (stored as assigned by that HM; you are recorded as who clicked)."
                    : "Select team members to assign this job"}
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-4 sm:px-6 py-4 space-y-4 transition-all duration-300 overflow-y-auto flex-1 min-h-0">
              {isLoadingAssignedUsers ? (
                <div className="">
                  <label className="text-sm font-medium text-gray-700 mb-3">Already Assigned</label>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className="flex items-center bg-gray-200 animate-pulse h-7 rounded-full"
                        style={{ width: `${80 + index * 20}px` }}
                      >
                      </div>
                    ))}
                  </div>
                </div>
              ) : (() => {
                const visibleAssigned = alreadyAssignedUsers.filter(
                  (user) => !canAssignHmOrHr || matchesAssignRoleTab(user.role)
                )
                if (visibleAssigned.length === 0) return null
                return (
                <div className="">
                  <label className="text-sm font-medium text-gray-700 mb-3">Already Assigned</label>
                  <div className="flex flex-wrap gap-2 mt-3 max-h-28 overflow-y-auto">
                    {visibleAssigned.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center bg-green-100 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full"
                      >
                        <span>
                          {user.name || user.username}
                          {user.role === "HR" &&
                            (user.managerUsername || user.assignedOnBehalfOfUsername) && (
                              <span className="text-green-600/80 font-normal">
                                {user.managerUsername
                                  ? ` · under ${user.managerUsername}`
                                  : ""}
                                {user.assignedOnBehalfOfUsername
                                  ? ` · by ${user.assignedOnBehalfOfUsername}`
                                  : ""}
                              </span>
                            )}
                        </span>
                        <button 
                          className="text-orange disabled:opacity-50" 
                          onClick={() => handleUnassignUser(user.id, selectedJob?.id ? selectedJob?.id : "")}
                          disabled={unassigningUserId === user.id}
                        >
                          {unassigningUserId === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                )
              })()}

          
              {/* Team Members Search and Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <label className="text-sm font-medium text-gray-700">Team Members</label>
                  {canAssignHmOrHr && (
                    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                      {(["HM", "HR"] as const).map((tab) => (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setAssignRoleTab(tab)}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                            assignRoleTab === tab
                              ? "bg-orange-500 text-white shadow-sm"
                              : "text-gray-600 hover:text-gray-900"
                          }`}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={
                      canAssignHmOrHr
                        ? assignRoleTab === "HR"
                          ? "Search HRs by name, email, or HM..."
                          : "Search HMs by name or email..."
                        : "Search by name or email..."
                    }
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all duration-200"
                    ref={inputRef}
                  />
                </div>
              </div>

              {(isSearchFocused || userSearchTerm.length > 0) && (
                <div className="border border-gray-200 rounded-2xl max-h-[min(240px,35vh)] overflow-y-auto bg-white">
                  {isLoadingAssignedUsers ? (
                    <div className="space-y-0">
                      {[1, 2, 3, 4, 5].map((index) => (
                        <div
                          key={index}
                          className="flex items-center p-3 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse mr-4"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + index * 5}%` }}></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${40 + index * 3}%` }}></div>
                          </div>
                          <div className="w-5 h-5 bg-gray-200 rounded-md animate-pulse"></div>
                        </div>
                      ))}
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => handleUserToggle(user.id)}
                        className="flex items-center p-3 hover:bg-orange-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-orange-500 rounded-full flex items-center justify-center text-white font-medium text-lg mr-4">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{user.username}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {user.email} • {user.role}
                            {user.role === "HR" && user.managerUsername
                              ? ` · under ${user.managerUsername}`
                              : ""}
                          </p>
                        </div>
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                            selectedUsers.includes(user.id)
                              ? "bg-orange-500 border-orange-500"
                              : "border-gray-300 bg-gray-50 group-hover:border-orange-400"
                          }`}
                        >
                          {selectedUsers.includes(user.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-8 text-center text-gray-500">
                      <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">
                        {canAssignHmOrHr
                          ? `No ${assignRoleTab}s found`
                          : "No team members found"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {selectedUsers.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-orange-600">
                    {selectedUsers.length} member{selectedUsers.length !== 1 ? "s" : ""} selected
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2 max-h-20 overflow-y-auto">
                    {getSelectedUserNames().map((username, index) => {
                      const userId = selectedUsers[index]
                      return (
                        <div
                          key={userId}
                          className="flex items-center bg-orange-100 text-orange-700 text-xs font-medium pl-2.5 pr-1 py-1 rounded-full"
                        >
                          <span>{username}</span>
                          <button
                            onClick={() => handleRemoveUser(userId)}
                            className="ml-1.5 p-0.5 rounded-full hover:bg-orange-200 focus:outline-none"
                            aria-label={`Remove ${username}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 sm:gap-4 p-4 sm:p-6 border-t border-gray-100 bg-gray-50 rounded-b-3xl shrink-0">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="flex-1 px-4 py-2.5 sm:py-3 text-gray-800 font-semibold bg-white rounded-xl border border-gray-300 hover:bg-gray-100 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignJob}
                disabled={selectedUsers.length === 0 || assigningJob}
                className="flex-1 px-4 py-2.5 sm:py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30 disabled:shadow-none"
              >
                {assigningJob ? "Assigning..." : "Assign Job"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Funnel Job IDs Dialog */}
      <Dialog
        open={funnelJobIdsDialogOpen}
        onOpenChange={(open) => {
          setFunnelJobIdsDialogOpen(open)
          if (!open) {
            setSelectedJobForFunnelIds(null)
            setCustomFunnelJobId("")
            setFunnelJobIdSearch("")
            setLoadingFunnelMappedJobs(false)
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Job ID</DialogTitle>
            <DialogDescription>
              {selectedJobForFunnelIds?.title || "Map this opening to AI Funnel"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Added job IDs for this job ({funnelMappedJobs.length})
              </p>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  value={funnelJobIdSearch}
                  onChange={(e) => setFunnelJobIdSearch(e.target.value)}
                  placeholder="Search job IDs"
                  className="pl-8 h-9 text-sm"
                />
              </div>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y">
                {loadingFunnelMappedJobs ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <div key={`funnel-job-id-shimmer-${index}`} className="px-3 py-2 space-y-2">
                      <div className="animate-pulse bg-gray-200 rounded w-40 h-4" />
                      <div className="animate-pulse bg-gray-200 rounded w-28 h-3" />
                    </div>
                  ))
                ) : filteredFunnelMappedJobs.length === 0 ? (
                  <p className="text-sm text-gray-500 p-3">
                    {funnelMappedJobs.length === 0 ? "No job IDs added yet." : "No matching job IDs."}
                  </p>
                ) : (
                  filteredFunnelMappedJobs.map((row) => (
                      <div key={row.sheetJobId} className="flex items-start justify-between gap-2 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 break-all">{row.sheetJobId}</p>
                          {row.jobTitle ? (
                            <p className="text-xs text-gray-500 truncate">{row.jobTitle}</p>
                          ) : null}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Add job ID</p>
              <div className="flex gap-2">
                <Input
                  value={customFunnelJobId}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => {
                    const next = e.target.value
                    if (next && !/^\d+$/.test(next)) {
                      toast.error("Sheet job ID must contain numbers only")
                      return
                    }
                    setCustomFunnelJobId(next)
                  }}
                  placeholder="Enter sheet job ID"
                  className="h-9 text-sm"
                />
                <Button
                  size="sm"
                  disabled={!selectedJobForFunnelIds?.id || !/^\d+$/.test(customFunnelJobId.trim()) || addingFunnelJobId !== null}
                  onClick={() =>
                    selectedJobForFunnelIds && handleAddFunnelJobId(selectedJobForFunnelIds, customFunnelJobId.trim())
                  }
                >
                  {addingFunnelJobId === customFunnelJobId.trim() ? "Adding..." : "Add"}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button variant="outline" onClick={() => setFunnelJobIdsDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Coordinators Dialog */}
      <Dialog open={coordinatorsDialogOpen} onOpenChange={setCoordinatorsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Coordinators</DialogTitle>
            <DialogDescription>
              {selectedJobForCoordinators?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {selectedJobForCoordinators?.coordinators && selectedJobForCoordinators.coordinators.length > 0 ? (
              selectedJobForCoordinators.coordinators.map((coordinator) => (
                <div key={coordinator.id} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                  <p className="font-medium text-gray-900 mb-2">{coordinator.username || 'Coordinator'}</p>
                  <div className="space-y-1">
                    {coordinator.email && (
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(coordinator.email)
                            toast.success('Email copied to clipboard!')
                          } catch (err) {
                            toast.error('Failed to copy email')
                          }
                        }}
                        title="Click to copy email"
                      >
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        <p className="text-sm text-gray-600">{coordinator.email}</p>
                      </div>
                    )}
                    {coordinator.phone && (
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:text-gray-900 transition-colors"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(coordinator.phone)
                            toast.success('Phone number copied to clipboard!')
                          } catch (err) {
                            toast.error('Failed to copy phone number')
                          }
                        }}
                        title="Click to copy phone number"
                      >
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        <p className="text-sm text-gray-600">{coordinator.phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No coordinators assigned</p>
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setCoordinatorsDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payments Dialog */}
      <Dialog open={paymentsDialogOpen} onOpenChange={setPaymentsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>
              {selectedJobForPayments?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Salary */}
            {selectedJobForPayments?.minSalary && selectedJobForPayments?.maxSalary && (
              <div className="border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-gray-500 text-lg">₹</span>
                  <p className="text-sm font-semibold text-gray-700">Salary Range</p>
                </div>
                <p className="text-sm text-gray-900 ml-6">
                  ₹{selectedJobForPayments.minSalary.toLocaleString()} - ₹{selectedJobForPayments.maxSalary.toLocaleString()}
                  {selectedJobForPayments.currency && ` ${selectedJobForPayments.currency}`}
                </p>
              </div>
            )}

            {/* Tenure */}
            {canViewPaymentDetails && selectedJobForPayments?.tenureInDays && (
              <div className="border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <p className="text-sm font-semibold text-gray-700">Tenure</p>
                </div>
                <p className="text-sm text-gray-900 ml-6">
                  {selectedJobForPayments.tenureInDays} days
                </p>
              </div>
            )}

            {/* Commission */}
            {canViewPaymentDetails && selectedJobForPayments?.commissionAmount && selectedJobForPayments?.commissionType && (
              <div className="pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-gray-500 text-lg">₹</span>
                  <p className="text-sm font-semibold text-gray-700">Commission</p>
                </div>
                <p className="text-sm text-gray-900 ml-6">
                  {selectedJobForPayments.commissionType === "fixed" 
                    ? `₹${((selectedJobForPayments.commissionAmount / 100) * 70).toFixed(2)} per joining`
                    : `${((selectedJobForPayments.commissionAmount / 100) * 50).toFixed(2)}% of Annual CTC`}
                </p>
              </div>
            )}

            {(!selectedJobForPayments?.minSalary && !selectedJobForPayments?.tenureInDays && !selectedJobForPayments?.commissionAmount) && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No payment details available</p>
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setPaymentsDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approvals Dialog */}
      <Dialog open={approvalsDialogOpen} onOpenChange={setApprovalsDialogOpen}>
        <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[90vh] h-[90vh] sm:max-h-[80vh] sm:h-[80vh] md:max-h-[70vh] md:h-[70vh] p-0 overflow-hidden flex flex-col">
          <div className="bg-white rounded-lg sm:rounded-3xl shadow-2xl w-full flex flex-col h-full min-h-0">
            <div className="flex items-center justify-between p-3 sm:p-4 md:p-6 border-b border-gray-100 flex-shrink-0">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
                  Job Approvals
                </DialogTitle>
                <DialogDescription className="text-xs sm:text-sm text-gray-500">
                  Review and manage job approvals
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <div className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 flex-shrink-0 space-y-2 sm:space-y-3">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by job title, company, or location..."
                    value={approvalSearchTerm}
                    onChange={(e) => setApprovalSearchTerm(e.target.value)}
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 min-h-0">
                  {(() => {
                    if (pendingApprovalJobs.length > 0) {
                      return (
                        <div className="space-y-1.5">
                          {pendingApprovalJobs.map((job) => (
                            <Card key={job.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                          <CardContent className="p-2 sm:p-2.5">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                                  <h3 className="text-xs sm:text-sm font-semibold text-gray-900 break-words">{job.title}</h3>
                                  <Badge variant="outline" className="text-xs w-fit bg-orange-100 text-orange-700">
                                    {job.status === "pendingApproval" ? "Pending Approval" : job.status === "rejected" ? "Rejected" : job.status}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2.5 text-xs text-gray-600">
                                  <div className="flex items-center gap-0.5">
                                    <Building2 className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate max-w-[100px] sm:max-w-none">{job.brandName}</span>
                                  </div>
                                  <div className="flex items-center gap-0.5">
                                    <MapPin className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate max-w-[80px] sm:max-w-none">{job.location}</span>
                                  </div>
                                 
                                
                                  <div className="flex items-center gap-0.5">
                                    <User className="h-3 w-3 flex-shrink-0" />
                                    <span>Posted by: {job.postedBy}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 w-full sm:w-auto mt-1 sm:mt-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setApprovalsDialogOpen(false)
                                    navigate(`/viewdetails/${job.id}`, {
                                      state: {
                                        fromApprovals: true,
                                        dashboardRestore: buildDashboardRestoreState(),
                                      },
                                    })
                                  }}
                                  className="flex-1 sm:flex-initial border-blue-300 text-blue-700 hover:bg-blue-50 text-xs px-2 py-1 h-7"
                                >
                                  <Eye className="mr-1 h-3 w-3" />
                                  Review
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedJobForAction(job)
                                    setActionType('approve')
                                    setConfirmationDialogOpen(true)
                                  }}
                                  className="flex-1 sm:flex-initial border-green-300 text-green-700 hover:bg-green-50 text-xs px-2 py-1 h-7"
                                >
                                  <Check className="mr-1 h-3 w-3" />
                                  Approve
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedJobForAction(job)
                                    setActionType('reject')
                                    setRejectComment("")
                                    setRejectCommentError("")
                                    setConfirmationDialogOpen(true)
                                  }}
                                  className="flex-1 sm:flex-initial border-red-300 text-red-700 hover:bg-red-50 text-xs px-2 py-1 h-7"
                                >
                                  <X className="mr-1 h-3 w-3" />
                                  Reject
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                            </Card>
                          ))}
                        </div>
                      )
                    }
                    
                    return (
                      <div className="text-center py-6 sm:py-8">
                        <ClipboardCheck className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                        <p className="text-xs sm:text-sm md:text-base text-gray-500 px-2">
                          {approvalSearchTerm ? "No jobs found matching your search" : "No jobs pending approval"}
                        </p>
                      </div>
                    )
                  })()}
              </div>
            </div>

            <div className="flex justify-end p-3 sm:p-4 md:p-6 border-t border-gray-100 flex-shrink-0">
              <Button variant="outline" onClick={() => setApprovalsDialogOpen(false)} className="w-full sm:w-auto text-sm sm:text-base px-4 sm:px-6 py-2">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Approve/Reject */}
      <Dialog open={confirmationDialogOpen} onOpenChange={setConfirmationDialogOpen}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {actionType === 'approve' 
                ? `Are you sure you want to approve "${selectedJobForAction?.title}"?`
                : `Are you sure you want to reject "${selectedJobForAction?.title}"? Please provide a reason.`
              }
            </DialogDescription>
          </DialogHeader>

          {actionType === 'reject' && (
            <div className="space-y-2 mt-4">
              <label className="text-sm font-medium text-gray-700">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Please provide a reason for rejecting this job..."
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value)
                  if (e.target.value.trim()) {
                    setRejectCommentError("")
                  }
                }}
                className="min-h-[100px] resize-none"
              />
              {rejectCommentError && (
                <p className="text-xs text-red-500">{rejectCommentError}</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setConfirmationDialogOpen(false)
                setSelectedJobForAction(null)
                setActionType(null)
                setRejectComment("")
                setRejectCommentError("")
              }}
            >
              Cancel
            </Button>
            <Button
              variant={actionType === 'approve' ? 'default' : 'destructive'}
              onClick={async () => {
                if (!selectedJobForAction?.id) return;

                if (actionType === 'reject') {
                  if (!rejectComment.trim()) {
                    setRejectCommentError("Rejection reason is required")
                    return
                  }
                  try {
                    await updateJobApprovalStatus(selectedJobForAction.id, 'rejected', rejectComment)
                    toast.success(`Job "${selectedJobForAction.title}" rejected successfully`)
                    if (isAdminUser) {
                      await fetchPendingApprovalJobs(approvalSearchTerm)
                    }
                  } catch (error: any) {
                    toast.error(error.response?.data?.message || `Failed to reject job: ${error.message}`)
                    return
                  }
                } else {
                  try {
                    await updateJobApprovalStatus(selectedJobForAction.id, 'published')
                    toast.success(`Job "${selectedJobForAction.title}" approved successfully`)
                    if (isAdminUser) {
                      await fetchPendingApprovalJobs(approvalSearchTerm)
                    }
                  } catch (error: any) {
                    toast.error(error.response?.data?.message || `Failed to approve job: ${error.message}`)
                    return
                  }
                }
                setConfirmationDialogOpen(false)
                setSelectedJobForAction(null)
                setActionType(null)
                setRejectComment("")
                setRejectCommentError("")
              }}
              className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {actionType === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  )
}

export default Dashboard